import t from 'react-translate';
import { css } from '@emotion/react';
import { gray2, gray3, primary } from 'styles/global_defaults/colors';
import { ReactComponent as UncheckedBox } from 'styles/icons/unchecked-box.svg';
import { ActionItemStatus } from 'redux/schemas/models/mentoring-program-action-items';
import { useFormContext } from 'react-hook-form';
import NvTooltip from 'shared/components/nv-tooltip';
import ClickableContainer from 'components/clickable-container';
import { config } from '@config/pendo.config.json';
import CheckedBox from './animated-checkmark';
import { CHECKBOX_SIZE } from './constants';
import { tooltipZIndex } from '../constants';

type ActionStatusProps = {
  actionItemId: string | number;
  status: ActionItemStatus;
  isReadOnly?: boolean;
};

const styles = css`
  &.unchecked {
    &:hover {
      color: ${gray2};
    }
  }
`;

const ActionStatus = ({
  actionItemId,
  status = ActionItemStatus.INCOMPLETE,
  isReadOnly = false,
}: ActionStatusProps) => {
  const { setValue } = useFormContext();
  const dataQATag = config.pendo.athena.mentorshipProgram.participantHome.actionItems.item
    .editActionItemStatus;
  const translationBase = t.MENTORING_PROGRAMS.PARTICIPANT_HOME.ACTION_ITEMS;

  const toggleStatus = () => {
    const newValue = status === ActionItemStatus.COMPLETED
      ? ActionItemStatus.INCOMPLETE
      : ActionItemStatus.COMPLETED;
    setValue('status', newValue);
  };

  const commonProps = {
    css: styles,
    width: CHECKBOX_SIZE,
    height: CHECKBOX_SIZE,
  };

  const statusIndicator = {
    [ActionItemStatus.INCOMPLETE]: {
      actionStatus: (
        <UncheckedBox {...commonProps} className='unchecked' color={gray3} />
      ),
      tooltip: translationBase.ITEM.TOOLTIP.MARK_COMPLETE(),
      ariaChecked: false,
    },
    [ActionItemStatus.COMPLETED]: {
      actionStatus: <CheckedBox {...commonProps} color={primary} />,
      tooltip: translationBase.ITEM.TOOLTIP.MARK_UNCOMPLETE(),
      ariaChecked: true,
    },
  };

  const { actionStatus, tooltip, ariaChecked } = statusIndicator[status];

  if (isReadOnly) {
    return <div className='d-flex'>{actionStatus}</div>;
  }

  return (
    <NvTooltip placement='top' text={tooltip} zIndex={tooltipZIndex}>
      <ClickableContainer
        className='d-flex'
        onClick={toggleStatus}
        role='checkbox'
        aria-checked={ariaChecked}
        aria-label={translationBase.ARIA_LABEL.ACTION_ITEM_STATUS()}
        data-qa={dataQATag}
        data-qa-id={`${dataQATag}-${actionItemId}-${status}`}
      >
        {actionStatus}
      </ClickableContainer>
    </NvTooltip>
  );
};

export default ActionStatus;
